/* eslint-disable @typescript-eslint/explicit-module-boundary-types,camelcase */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import server from 'src/config/server'
import { getIP } from '@jarvis.oddle/core/tools/ip'
import uuid from 'uuid/v4'
import { getCookie, stripSecureFlag } from '@jarvis.oddle/core/tools/cookie'
import { parseJwt } from 'tools/jwtParser'
import { ODDLE_PASS_CLIENT_ID, ODDLE_PASS_REDIRECT_URL } from 'config/client'
import { NextPageContext } from 'next'
import { Request, Response } from 'express'
import { isEmpty } from 'src/utils/string'
import logger from '@jarvis.oddle/core/tools/logger'
import { postLogApi } from '@jarvis.oddle/core/api/newrelic/client'
import { logApiError } from '@jarvis.oddle/core/tools/error'
import { API_ROOT as DEFAULT_APP_API_ROOT, origin } from 'config/server'
import { retrieveCookies } from 'src/utils/function'
import {
  dateToRequestString,
  getCurrentUrl,
  makeQueryString
} from 'src/utils/routes'

export type RequestType = NextPageContext['req'] & {
  appVersion: string
  cartName: string
} & Request

export type ResponseType = NextPageContext['res'] & {
  redirect: (path: string) => void
} & Response

export type ExtendAxiosRequestConfig = AxiosRequestConfig & {
  time: {
    startTime: Date
    endTime?: Date
  }
}

export type ExtendAxiosResponse = AxiosResponse & {
  duration?: number
  response?: AxiosResponse
}

const getBaseUrl = (isServer: boolean) =>
  isServer ? server.v3ApiTarget : '/api/fesc/v1'

const serverDomain = server.omsApiV4Endpoint.replace(/(^\w+:|^)\/\//, '')

const getAccessToken = (req): string => {
  const enableOddlePass =
    getCookie(retrieveCookies(req), 'enableOddlePass') === 'true'
  let accessToken
  if (
    typeof window !== 'undefined' &&
    window.localStorage.getItem('accessToken') &&
    enableOddlePass
  ) {
    const parsedToken = parseJwt(window.localStorage.getItem('accessToken'))
    if (parsedToken.exp >= new Date().getTime() / 1000) {
      accessToken = window.localStorage.getItem('accessToken')
    } else {
      window.localStorage.removeItem('accessToken')
    }
  }

  return accessToken
}

export const makeHeaders = ({
  req,
  clientId
}: {
  req?: RequestType
  clientId?: string
}) => {
  const accessToken = getAccessToken(req)
  let extendHeader
  if (req) {
    extendHeader = {
      Cookie: req.get?.('Cookie') || req.headers?.cookie || '',
      'User-Agent': req.get?.('User-Agent') || req.headers?.['user-agent'],
      Referer: req.get?.('Referer') || req.headers?.referer || '',
      'X-Forwarded-For': getIP(req),
      'Custom-Url': getCookie(
        req.get?.('Cookie') || req.headers?.cookie || '',
        'Custom-Url'
      )
    }
  } else if (typeof window !== 'undefined') {
    extendHeader = {
      Cookie: window.document.cookie
    }
  }
  return {
    ...extendHeader,
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // Custom header for OMS to identify the caller page URL
    // There is no standard header that fit our use case: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers
    'X-Request-Id': uuid(),
    ...(accessToken
      ? {
          'X-Oddle-Pass-Token': accessToken,
          'X-Oddle-Pass-Client-Id': clientId || ODDLE_PASS_CLIENT_ID
        }
      : {})
  }
}

const axiosInstance = axios.create({
  validateStatus: (status) => {
    return status >= 200 && status < 400
  }
})

axiosInstance.interceptors.request.use(
  function (req: ExtendAxiosRequestConfig) {
    req.time = { startTime: new Date() }

    const storeOrderId = getCookie(req.headers.Cookie, 'storeOrderId') || ''
    const visitorId = getCookie(req.headers.Cookie, 'vid') || ''

    if (req.url.includes('?') && !req.url.includes('_soid')) {
      req.url += `${
        storeOrderId
          ? `&_soid=${storeOrderId}&_tid=${visitorId + Date.now().toString()}`
          : ''
      }`
    } else if (!req.url.includes('_soid')) {
      req.url += `${
        storeOrderId
          ? `?_soid=${storeOrderId}&_tid=${visitorId + Date.now().toString()}`
          : ''
      }`
    }
    return req
  },
  (err) => {
    logApiError({
      response: {
        ...err,
        status: err.status,
        statusCode: err.status,
        url: err.config.url,
        request: {
          uri: {
            href: err.config.url
          }
        }
      },
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      extendLogs: `token ${err.config.headers['x-oddle-pass-token']} and got`
    })
    return err
  }
)

axiosInstance.interceptors.response.use(
  function (res: ExtendAxiosResponse) {
    ;(res.config as ExtendAxiosRequestConfig).time.endTime = new Date()
    res.duration =
      (res.config as ExtendAxiosRequestConfig).time.endTime.getTime() -
      (res.config as ExtendAxiosRequestConfig).time.startTime.getTime()

    return res
  },
  (err) => {
    logApiError({
      response: {
        ...err,
        status: err.status,
        statusCode: err.status,
        url: err.config.url,
        request: {
          uri: {
            href: err.config.url
          }
        }
      },
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      extendLogs: `token ${err.config.headers['x-oddle-pass-token']} and got`
    })
    throw err
  }
)

// Will use this function (passToLogger), when proxy to backend server later.
// Will handle in the separate ticket.
// Borrowed from fesc/api/server.js -> request function
const passToLogger = async ({
  req,
  res,
  response,
  options,
  logsRequest = false,
  uri,
  payload,
  qs
}: {
  req: RequestType
  res: ResponseType
  response: AxiosResponse & Response
  options: ReturnType<typeof makeHeaders>
  logsRequest?: boolean
  uri: string
  payload?: Record<string, unknown>
  qs?: Record<string, string>
}) => {
  const accessToken = getAccessToken(req)
  const requestCookie = req.get('Cookie') || ''
  const storeOrderId = getCookie(requestCookie, 'storeOrderId') || ''
  const identifiedQs = {
    ...qs,
    _soid: !isEmpty(storeOrderId) ? storeOrderId : ''
  }

  logger.verbose(
    `[visitorid ${req.cookies && req.cookies.visitorid}] ${
      req.url
    } requested ${JSON.stringify(options, null, 2)}`
  )
  const isHTML = Array.isArray(response.data)
    ? response.data.indexOf('<!DOCTYPE html>') > -1
    : false
  logger.verbose(`[visitorid ${req.cookies && req.cookies.visitorid}] ${req.url}
  got response ${isHTML ? '<html>...</html>' : response.data} ${
    response.statusCode
  }
  ${isHTML ? `with cookie ${response.headers['set-cookie']}` : ''}
`)
  // log all request for specific api
  if (logsRequest) {
    postLogApi({
      req,
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      payload: {
        message: `fesc requested ${JSON.stringify({
          ...identifiedQs,
          uri: uri,
          body: payload
        })} got response ${response.data} ${response.statusCode}`.substring(
          0,
          1000
        )
      }
    }).catch((_) => _)
  } else {
    await logApiError({
      req,
      response,
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      extendLogs: `token ${accessToken} and got`
    })
  }

  const cookie = stripSecureFlag(response.headers['set-cookie'])
  if (cookie) res.setHeader('set-cookie', cookie)

  const cacheControl = response.headers['cache-control']
  if (cacheControl) res.setHeader('cache-control', cacheControl)

  const pragma = response.headers['pragma']
  if (pragma) res.setHeader('pragma', pragma)

  return response
}

// TODO: refactor this when migration getInitialProps to getServerSideProps & getStaticProps
export const getTermsAndConditions = (menuId: string, isServer: boolean) =>
  axiosInstance.get<Oms.V4.ApiResponse<string>>(
    `${getBaseUrl(isServer)}/api/v4/cart/${menuId}/terms-and-conditions`
  )

export const getPrivacyPolicy = (menuId: string, isServer: boolean) =>
  axiosInstance.get<Oms.V4.ApiResponse<string>>(
    `${getBaseUrl(isServer)}/api/v4/cart/${menuId}/privacy-policy`
  )

export const getListOfMenuSite = async ({
  slug,
  req,
  query
}: {
  slug: string
  req: RequestType
  query?: Record<string, string | string[]>
}): Promise<ExtendAxiosResponse> => {
  const getHeaders = makeHeaders({ req })
  let cartName = slug
  if (isEmpty(slug)) {
    cartName = getCurrentUrl(req, req.originalUrl).match(
      /\/\/((.*?)-(.*?)--(.*?))\.test\.(.*)/i
    )?.[1]
  }
  // this changing support OMS test, we will have different endpoint for each test version, the endpoint seem like: https://api-OMS-0129.test.oddle.me
  let domain = serverDomain
  if (domain.includes('.test.oddle.me')) {
    domain = 'test.oddle.me'
  }
  if (`${cartName}.${domain}` === 'undefined.beta.oddle.me') {
    console.warn(
      `Calling from server with ${JSON.stringify({
        cartName: req.cartName,
        hostname: req.hostname,
        headers: req.headers
      })}`
    )
  }
  return axiosInstance.get<Oms.V4.ApiResponse<Meta.ListOfMenuSite>>(
    `https://${cartName}.${domain}/api/v5/cart${makeQueryString({
      referrer: req?.query?.referrer,
      ...(query?.rwg_token
        ? {
            'rwg-token': query.rwg_token,
            'merchant-id': query.merchant_id
          }
        : {})
    })}`,
    {
      headers: {
        ...getHeaders,
        'Loaded-Url': getCurrentUrl(req, req.originalUrl)
      }
    }
  )
}

export const getDetailMenuSite = async ({
  menuId,
  slug,
  req,
  mode
}: {
  menuId: string
  slug: string
  req: RequestType
  mode: string
}) => {
  const getHeaders = makeHeaders({ req })
  let cartName = slug
  if (isEmpty(slug)) {
    cartName = getCurrentUrl(req, req.originalUrl).match(
      /\/\/((.*?)-(.*?)--(.*?))\.test\.(.*)/i
    )?.[1]
  }
  // this changing support OMS test, we will have different endpoint for each test version, the endpoint seem like: https://api-OMS-0129.test.oddle.me
  let domain = serverDomain
  if (domain.includes('test.oddle.me')) {
    domain = 'test.oddle.me'
  }
  return axiosInstance.get<Oms.V4.ApiResponse<Meta.MetaData>>(
    `https://${cartName}.${domain}/api/v5/cart/menus/${menuId}${makeQueryString(
      { referrer: req?.query?.referrer, mode }
    )}`,
    {
      headers: {
        ...getHeaders,
        'Loaded-Url': getCurrentUrl(req, req.originalUrl)
      }
    }
  )
}

export const getMenuFeaturesAction = async ({
  menuId,
  features,
  isServer,
  req
}: {
  menuId: string
  features: string[]
  isServer: boolean
  req: RequestType
}) => {
  const queryString = features.reduce(
    (string, feature) => `${string}&features%5B%5D=${feature}`,
    ''
  )
  const getHeaders = makeHeaders({ req })

  return axiosInstance.get<Oms.V4.ApiResponse<Record<string, boolean>>>(
    `${getBaseUrl(isServer)}/api/v4/menus/${menuId}/features?${queryString}`,
    {
      headers: getHeaders
    }
  )
}

export const getAddressFormatting = async ({
  menuId,
  isServer,
  req
}: {
  menuId: string
  isServer: boolean
  req: RequestType
}) => {
  const getHeaders = makeHeaders({ req })
  return axiosInstance.get<
    Oms.V4.ApiResponse<{
      addressTwoFormat: Oms.V4.AddressTwoFormat[]
      autocompleteAddressFields: string[][]
    }>
  >(`${getBaseUrl(isServer)}/api/v4/cart/${menuId}/address-format`, {
    headers: getHeaders
  })
}

export const getTrackerAPi = async ({
  menuId,
  isServer,
  req
}: {
  menuId: string
  isServer: boolean
  req: RequestType
}) => {
  const getHeaders = makeHeaders({ req })
  return axiosInstance.get<Oms.V4.ApiResponse<Meta.Trackers[]>>(
    `${getBaseUrl(isServer)}/cart-api-v3/menus/${menuId}/analytics-tracker`,
    {
      headers: getHeaders
    }
  )
}

export const getScriptsApi = async ({
  menuId,
  isServer,
  req
}: {
  menuId: string
  isServer: boolean
  req: RequestType
}) => {
  const getHeaders = makeHeaders({ req })
  return axiosInstance.get<Meta.Scripts>(
    `${getBaseUrl(
      isServer
    )}/cart-api-v3/ajax-get-shopping-cart-trackers?menu-id=${menuId}`,
    {
      headers: getHeaders
    }
  )
}

export const getMenuApi = async ({
  req,
  isServer = false,
  menuId,
  storeOrderId,
  mode
}: {
  menuId: string
  storeOrderId: string
  req: RequestType
  isServer: boolean
  mode: string
}) => {
  const getHeaders = makeHeaders({ req })
  const path = isServer
    ? `/cart-api-v3/ajax-retrieve-menu?menu-id=${menuId}&store-order-id=${storeOrderId}&mode=${mode}`
    : `/menu/${menuId}?storeOrderId=${storeOrderId}&mode=${mode}`
  return axiosInstance.get(`${getBaseUrl(isServer)}${path}`, {
    headers: getHeaders
  })
}

// call from init props
export const getStripeTokenApi = async ({
  menuId,
  testAccount
}: {
  menuId: string
  testAccount: boolean
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.get(
    `${getBaseUrl(true)}/api/v4/payment-options/stripe-token?menuId=${menuId}${
      testAccount ? `&testAccount=${testAccount}` : ''
    }`,
    {
      headers: getHeaders
    }
  )
}

// call from init props
export const getInvoiceMetaApi = async ({
  storeOrderId,
  hashCode
}: {
  storeOrderId: string
  hashCode?: string
}) =>
  axiosInstance.get(
    `${getBaseUrl(true)}/api/v4/invoice/${storeOrderId}/${hashCode}`,
    {
      headers: makeHeaders({})
    }
  )

// call from init props
export const getInvoicePaymentMethodsApi = async ({
  storeOrderId
}: {
  storeOrderId: string
}) =>
  axiosInstance.get(
    `${getBaseUrl(
      true
    )}/checkout/cart-v3/ajax-retrieve-online-payment-options?store-order-id=${storeOrderId}`,
    {
      headers: makeHeaders({})
    }
  )

export const checkoutApi = async (payload) =>
  axiosInstance.post(
    `${getBaseUrl(false)}/order/${payload.storeOrderId}/checkout`,
    payload,
    {
      headers: makeHeaders({})
    }
  )

// this calling should from server to server
export const getStoreOrderApi = async ({
  storeOrderId
}: {
  storeOrderId: string
}) =>
  axiosInstance.get(`${origin()}${getBaseUrl(false)}/order/${storeOrderId}`, {
    headers: makeHeaders({})
  })

export const getEligiblePromotionsApi = async ({
  storeOrderId
}: {
  storeOrderId: string
}) =>
  axiosInstance.get(`${getBaseUrl(false)}/order/${storeOrderId}/promotions`, {
    headers: makeHeaders({})
  })

export const updateStoreOrderItemQuantityApi = async ({
  storeOrderId,
  items
}: {
  storeOrderId: string
  items
}) =>
  axiosInstance.put(
    `${getBaseUrl(false)}/cart-api-v3/ajax-update-multiple-order-item-quantity`,
    {
      storeOrderId,
      items
    },
    {
      headers: makeHeaders({})
    }
  )

export const addToCartApi = async ({
  storeOrderId,
  orderItem
}: {
  storeOrderId: string
  orderItem: {
    variantId: string
    quantity: number
    subitems: Record<string, unknown>[]
  }
}) =>
  axiosInstance.post(
    `${getBaseUrl(false)}/order/${storeOrderId}/items`,
    {
      ...orderItem
    },
    {
      headers: makeHeaders({})
    }
  )

export const clearCartApi = async ({
  storeOrderId
}: {
  storeOrderId: string
}) =>
  axiosInstance.delete(
    `${origin()}${getBaseUrl(false)}/order/${storeOrderId}/items`
  )

export const getTimeSlotsApi = async ({
  storeOrderId,
  date
}: {
  storeOrderId: string
  date: string | Date
}) =>
  axiosInstance.get(
    `${getBaseUrl(false)}/order/${storeOrderId}/slots/${dateToRequestString(
      date
    )}`,
    {
      headers: makeHeaders({})
    }
  )

export const updateCollectionApi = async ({
  storeOrderId,
  method
}: {
  storeOrderId: string
  method: string
}) =>
  axiosInstance.put(
    `${getBaseUrl(false)}/order/${storeOrderId}/collection`,
    {
      isPickup: method === 'pickup'
    },
    {
      headers: makeHeaders({})
    }
  )

export const updatePickupLocationApi = async ({
  storeOrderId,
  storeId,
  lat,
  long,
  selectedDate
}: {
  storeOrderId: string
  storeId?: string
  lat?: string
  long?: string
  selectedDate?: string
}) =>
  axiosInstance.put(
    `${getBaseUrl(false)}/order/${storeOrderId}/store`,
    {
      storeId,
      lat,
      long,
      selectedDate
    },
    {
      headers: makeHeaders({})
    }
  )

export const updateDeliveryLocationApi = async ({
  address,
  storeOrderId
}: {
  address: {
    selectedAddressId?: string
    deliveryAddress: string
    deliveryAddressOne: string
    deliveryAddressTwo: string
    deliveryAddressCity?: string
    deliveryAddressState?: string
    deliveryAddressCountry: string
    deliveryAddressPostal: string
    latitude: number | string
    longitude: number | string
    addressTwoDetails?: Record<string, string>
  }
  storeOrderId: string
}) =>
  axiosInstance.put(
    `${getBaseUrl(false)}/order/${storeOrderId}/address`,
    {
      storeOrderId,
      address
    },
    {
      headers: makeHeaders({})
    }
  )

export const updateDeliveryAddressTwoApi = async ({
  storeOrderId,
  addressTwo
}: {
  addressTwo: string
  storeOrderId: string
}) =>
  axiosInstance.put(
    `${getBaseUrl(false)}/order/${storeOrderId}/addressTwo`,
    { id: storeOrderId, customerAddressTwo: addressTwo },
    {
      headers: makeHeaders({})
    }
  )

export const updateCollectionTimeApi = async ({
  collectionDate,
  collectionTimeSlot,
  collectionTimeSlotId,
  storeOrderId
}: {
  collectionDate: string
  collectionTimeSlot: string
  collectionTimeSlotId: string
  storeOrderId: string
}) =>
  axiosInstance.put(
    `${getBaseUrl(false)}/order/${storeOrderId}/slots`,
    {
      collectionDate: dateToRequestString(collectionDate),
      collectionTimeSlot,
      collectionTimeSlotId
    },
    {
      headers: makeHeaders({})
    }
  )

export const applyPromotionsApi = async ({
  promotions,
  storeOrderId
}: {
  promotions: {
    promotionId: string
    variants: {
      variantId: string
      subitems: Record<string, unknown>[]
      quantity: number
    }[]
  }[]
  storeOrderId: string
}) =>
  axiosInstance.post(
    `${getBaseUrl(false)}/order/${storeOrderId}/promotions`,
    {
      promotions
    },
    {
      headers: makeHeaders({})
    }
  )

export const getPromoCodeApi = async ({
  code,
  storeOrderId
}: {
  code: string
  storeOrderId: string
}) =>
  axiosInstance.get(
    `${getBaseUrl(false)}/order/${storeOrderId}/promotions/${encodeURIComponent(
      code
    )}`,
    {
      headers: makeHeaders({})
    }
  )

export const prepareCheckoutApi = async ({
  storeOrderId
}: {
  storeOrderId: string
}) => {
  return axiosInstance.get(
    `${getBaseUrl(false)}/order/${storeOrderId}/checkout/prepare`,
    {
      headers: makeHeaders({})
    }
  )
}

export const getAllStoresApi = async ({
  req,
  isServer = false,
  menuId
}: {
  menuId: string
  req: RequestType
  isServer: boolean
}) => {
  const getHeaders = makeHeaders({ req })
  const path = isServer
    ? `${server.v3ApiTarget}/cart-api-v3/ajax-retrieve-all-stores?menu-id=${menuId}`
    : `${origin()}${getBaseUrl(false)}/menu/${menuId}/stores`
  return axiosInstance.get(path, {
    headers: getHeaders
  })
}

export const getPickupStoresApi = async ({
  storeId,
  storeOrderId
}: {
  storeId: string
  storeOrderId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.get(
    `${getBaseUrl(false)}/order/${storeOrderId}/stores${makeQueryString({
      storeId
    })}`,
    {
      headers: getHeaders
    }
  )
}

// call from init props
export const getAvailablePromotionsApi = async ({
  storeOrderId,
  mode
}: {
  storeOrderId: string
  mode: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.get(
    `${getBaseUrl(
      true
    )}/cart-api-v3/ajax-retrieve-available-promotions?store-order-id=${storeOrderId}&mode=${mode}`,
    {
      headers: getHeaders
    }
  )
}

// call from init props
export const loadStoreItemAvailabilityApi = async ({
  menuId
}: {
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.get(
    `${getBaseUrl(true)}/api/v4/unavailableResources/menus/${menuId}`,
    {
      headers: getHeaders
    }
  )
}

export const newsletterSignupApi = async ({
  email,
  firstName,
  lastName,
  menuId
}: {
  email: string
  firstName: string
  lastName: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${getBaseUrl(false)}/user/newsletter/subscription`,
    { email, firstName, lastName, menuId },
    {
      headers: getHeaders
    }
  )
}

export const postStripeChargesApi = async ({
  sourceId,
  storeOrderId
}: {
  sourceId: string
  storeOrderId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${getBaseUrl(false)}/api/stripe/charges`,
    {
      sourceId,
      storeOrderId
    },
    {
      headers: getHeaders
    }
  )
}

export const getOPAccessTokenApi = async ({
  menuId,
  customerId
}: {
  menuId: string
  customerId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/oauth2/customer-access`,
    {
      clientId: ODDLE_PASS_CLIENT_ID,
      redirectUri: ODDLE_PASS_REDIRECT_URL,
      menuId,
      customerId,
      state: { action: 're-new accessToken from Eats' }
    },
    {
      headers: getHeaders
    }
  )
}

export const userLoginApi = async ({
  cartName,
  email,
  password,
  source,
  storeOrderId,
  menuId
}: {
  email: string
  password: string
  source: string
  cartName: string
  storeOrderId: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/ajax-login?subdomain=${cartName}`,
    { email, source, password, storeOrderId, menuId },
    {
      headers: getHeaders
    }
  )
}

export const fetchOcaUserApi = async ({
  cartName,
  email,
  password,
  menuId
}: {
  email: string
  password: string
  cartName: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/user/ocaUser/?subdomain=${cartName}`,
    { email, password, menuId },
    {
      headers: getHeaders
    }
  )
}

export const userLogoutApi = async ({
  cartName,
  storeOrderId
}: {
  cartName: string
  storeOrderId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/ajax-logout?subdomain=${cartName}`,
    { storeOrderId },
    {
      headers: getHeaders
    }
  )
}

export const createUserApi = async ({
  cartName,
  email,
  firstName,
  lastName,
  password,
  phone,
  storeOrderId,
  menuId,
  facebookId,
  source,
  gender,
  dob,
  hash,
  receiveEmail,
  acceptMarketing,
  acceptMarketingMenu
}: {
  cartName: string
  email: string
  firstName: string
  lastName: string
  password: string
  phone: string
  storeOrderId: string
  menuId: string
  facebookId: string
  source: string
  gender: string
  dob: string
  hash: string
  receiveEmail: boolean
  acceptMarketing: boolean
  acceptMarketingMenu: boolean
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/user?subdomain=${cartName}`,
    {
      id: '',
      company: '',
      email,
      firstName,
      lastName,
      password,
      phone,
      storeOrderId,
      menuId,
      facebookId,
      source,
      gender,
      dob: dateToRequestString(dob),
      hash,
      receiveEmail,
      acceptMarketing,
      acceptMarketingMenu
    },
    {
      headers: getHeaders
    }
  )
}

export const facebookLoginApi = async ({
  cartName,
  storeOrderId,
  hash
}: {
  cartName: string
  storeOrderId: string
  hash: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/facebook-login-success?cartName=${cartName}`,
    { storeOrderId, hash },
    {
      headers: getHeaders
    }
  )
}

export const getUserApi = async ({
  cartName,
  clientId
}: {
  cartName: string
  clientId: string
}) => {
  const getHeaders = makeHeaders({ clientId })
  return axiosInstance.get(
    `${origin()}${getBaseUrl(false)}/user?subdomain=${cartName}`,
    {
      headers: getHeaders
    }
  )
}

export const updateUserApi = async ({
  payload: {
    email,
    salutation,
    firstName,
    lastName,
    phone,
    additionalPhone,
    organisationName,
    receiveEmail,
    gender,
    dob,
    source,
    customerId,
    menuId,
    acceptMarketing,
    acceptMarketingMenu
  },
  cartName
}: {
  payload: {
    dob: string
    phone: string
    customerId: string
    organisationName: string
    gender: string
    receiveEmail: boolean
    acceptMarketing: boolean
    email: string
    salutation: string
    additionalPhone: string
    acceptMarketingMenu: boolean
    source: string
    menuId: string
    lastName: string
    firstName: string
  }
  cartName: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/ajax-update-personal-settings?subdomain=${cartName}`,
    {
      email,
      salutation,
      firstName,
      lastName,
      phone,
      additionalPhone,
      organisationName,
      receiveEmail,
      gender,
      source,
      customerId,
      menuId,
      acceptMarketing,
      acceptMarketingMenu,
      dob: dateToRequestString(dob)
    },
    {
      headers: getHeaders
    }
  )
}

export const updateUserPasswordApi = async ({
  cartName,
  customerId,
  menuId,
  newPassword,
  reNewPassword,
  password
}: {
  customerId: string
  menuId: string
  newPassword: string
  reNewPassword: string
  password: string
  cartName: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/ajax-update-password?subdomain=${cartName}`,
    {
      customerId,
      menuId,
      newPassword,
      reNewPassword,
      password
    },
    {
      headers: getHeaders
    }
  )
}

export const userResetPasswordApi = async ({
  cartName,
  email,
  menuId
}: {
  cartName: string
  email: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/ajax-send-reset-password-instructions?subdomain=${cartName}`,
    {
      menuId,
      email
    },
    {
      headers: getHeaders
    }
  )
}

export const setDefaultUserDeliveryAddressApi = async ({
  addressId,
  customerId,
  menuId
}: {
  addressId: string
  customerId: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/ajax-set-default-billing-delivery-address`,
    { addressId, customerId, menuId },
    {
      headers: getHeaders
    }
  )
}

export const createUserDeliveryAddressApi = async ({
  cartName,
  customerId,
  address
}: {
  cartName: string
  customerId: string
  address: {
    autoCompleteAddress: string
    address: string
    addressTwo: string
    city: string
    state: string
    country: string
    postal: string
    latitude: string
    longitude: string
  }
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/customer-api-v3/ajax-create-address`,
    { address, customerId, cartName },
    {
      headers: getHeaders
    }
  )
}

export const updateUserDeliveryAddressApi = async ({
  customerId,
  address
}: {
  customerId: string
  address: {
    id: string
    autoCompleteAddress: string
    addressTwo: string
    city: string
    state: string
    country: string
    postal: string
    latitude: string
    longitude: string
  }
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/customer-api-v3/ajax-update-address`,
    { address, customerId },
    {
      headers: getHeaders
    }
  )
}

export const removeUserDeliveryAddressApi = async ({
  addressId,
  customerId
}: {
  addressId: string
  customerId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/customer-api-v3/ajax-remove-address`,
    { addressId, customerId },
    {
      headers: getHeaders
    }
  )
}

export const checkUserExistenceApi = async ({
  userEmail,
  source,
  menuId
}: {
  userEmail: string
  source: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.get(
    `${origin()}${getBaseUrl(false)}/customer-api-v3/users${makeQueryString({
      menuId,
      email: userEmail,
      source
    })}`,
    {
      headers: getHeaders
    }
  )
}

export const verifyOTPApi = async ({
  OTP,
  OTPSessionId,
  phone,
  menuId
}: {
  OTP: string
  OTPSessionId: string
  phone: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/api/loyalty/zap/otp/verify?menuId=${menuId}`,
    {
      OTP,
      OTPSessionId,
      phone
    },
    {
      headers: getHeaders
    }
  )
}

export const resendOTPApi = async ({
  OTPSessionId,
  menuId
}: {
  OTPSessionId: string
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/api/loyalty/zap/otp/resend?menuId=${menuId}`,
    {
      OTPSessionId
    },
    {
      headers: getHeaders
    }
  )
}

export const sendOTPApi = async ({
  purpose,
  menuId,
  phone
}: {
  purpose?: string
  menuId: string
  phone: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/api/loyalty/zap/otp/send?menuId=${menuId}`,
    {
      purpose,
      phone
    },
    {
      headers: getHeaders
    }
  )
}

export const createLoyaltyCustomerApi = async ({
  customerId,
  menuId
}: {
  menuId: string
  customerId: string
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.post(
    `${origin()}${getBaseUrl(
      false
    )}/api/loyalty/zap/customer/register?menuId=${menuId}`,
    {
      customerId
    },
    {
      headers: getHeaders
    }
  )
}

export const fetchUserViaOddlePassApi = async ({
  cartName,
  loggedOut
}: {
  cartName: string
  loggedOut?: boolean
}) => {
  const getHeaders = makeHeaders({})
  return axiosInstance.get(
    `${origin()}${getBaseUrl(
      false
    )}/customer-api-v3/ajax-retrieve-customer${makeQueryString({
      subdomain: cartName,
      loggedOut
    })}`,
    {
      headers: getHeaders
    }
  )
}

export const getOPAddressesApi = async () => {
  let accessToken
  if (
    typeof window !== 'undefined' &&
    window.localStorage.getItem('accessToken')
  ) {
    const parsedToken = parseJwt(window.localStorage.getItem('accessToken'))
    if (parsedToken.exp >= new Date().getTime() / 1000) {
      accessToken = window.localStorage.getItem('accessToken')
    } else {
      window.localStorage.removeItem('accessToken')
    }
  }
  return axiosInstance.get(`${origin()}${getBaseUrl(false)}/addresses`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Request-Id': uuid(),
      'X-Oddle-Pass-Token': accessToken,
      'X-Oddle-Pass-Client-Id': ODDLE_PASS_CLIENT_ID
    }
  })
}

export const getCheckoutMetaApi = async ({
  storeOrderId,
  req,
  isServer
}: {
  storeOrderId: string
  req: RequestType
  isServer: boolean
}) => {
  const path = isServer
    ? `${server.v3ApiTarget}/api/v4/cart/${storeOrderId}`
    : `${origin()}${getBaseUrl(false)}/order/${storeOrderId}/checkout`
  return axiosInstance.get(path, {
    headers: makeHeaders({ req })
  })
}

// call from init props
export const getCheckoutPaymentMethodsApi = async ({
  storeOrderId,
  req
}: {
  storeOrderId: string
  req: RequestType
  isServer: boolean
}) =>
  axiosInstance.get(
    `${getBaseUrl(
      true
    )}/checkout/cart-v3/ajax-retrieve-payment-options?store-order-id=${storeOrderId}`,
    {
      headers: makeHeaders({ req })
    }
  )

export const updateDeliveryDetailsApi = async ({
  storeOrderId,
  deliveryAddressType,
  deliveryAddressTwoDetails,
  deliveryInstructions
}: {
  storeOrderId: string
  deliveryAddressType: string
  deliveryAddressTwoDetails
  deliveryInstructions
}) =>
  axiosInstance.put(
    `${getBaseUrl(false)}/api/v4/cart/${storeOrderId}/delivery-details`,
    { deliveryAddressType, deliveryAddressTwoDetails, deliveryInstructions },
    {
      headers: makeHeaders({})
    }
  )

export const getStoreOrderMetaDataApi = async ({
  storeOrderId
}: {
  storeOrderId: string
}) =>
  axiosInstance.get(
    `${origin()}${getBaseUrl(false)}/api/v4/orders/${storeOrderId}/metadata`,
    {
      headers: makeHeaders({})
    }
  )

export const getListOfVouchersApi = async ({
  customer,
  order
}: {
  customer?: {
    source_id: string
    email: string
  }
  order?: {
    source_id: string
    amount: number
    items: {
      product_id: string
      quantity: number
      price: number
    }[]
    metadata: {
      menuId: string
      isPickup: boolean
      oddlePassFirstOrder: boolean
      oddlePassFirstMenuOrder: boolean
      fulfilmentDate: string
      submissionDate: string
      cardBin: string
    }
  }
}) =>
  axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/vouchers/qualifications`,
    {
      customer,
      order
    },
    {
      headers: makeHeaders({})
    }
  )

export const validateVoucherApi = async ({
  customer,
  order,
  code
}: {
  customer?: {
    source_id: string
    email: string
  }
  order?: {
    source_id: string
    amount: number
    items: {
      product_id: string
      quantity: number
      price: number
    }[]
    metadata: {
      menuId: string
      isPickup: boolean
      oddlePassFirstOrder: boolean
      oddlePassFirstMenuOrder: boolean
      fulfilmentDate: string
      submissionDate: string
      cardBin: string
    }
  }
  code: string
}) =>
  axiosInstance.post(
    `${origin()}${getBaseUrl(false)}/vouchers/${code}/validate`,
    {
      customer,
      order
    },
    {
      headers: makeHeaders({})
    }
  )

export const getCheckoutResultsApi = async ({
  req,
  storeOrderId,
  isServer
}: {
  req: RequestType
  storeOrderId: string
  isServer: boolean
}) =>
  axiosInstance.get(
    `${isServer ? origin() : ''}${getBaseUrl(
      isServer
    )}/retrieve-store-order-and-payment-status?storeOrderId=${storeOrderId}`,
    {
      headers: makeHeaders({ req })
    }
  )

export const verifyAuthenticateTokenApi = async ({
  req,
  storeOrderId,
  token,
  isServer
}: {
  req: RequestType
  storeOrderId: string
  token: string
  isServer: boolean
}) =>
  axiosInstance.post(
    `${isServer ? origin() : ''}${getBaseUrl(
      isServer
    )}/api/v4/orders/${storeOrderId}/authenticate-token`,
    {
      token
    },
    {
      headers: makeHeaders({ req })
    }
  )

export const getDeliveryTrackingLinkApi = async ({
  req,
  storeOrderId,
  isServer
}: {
  req: RequestType
  storeOrderId: string
  isServer: boolean
}) =>
  axiosInstance.get(
    `${isServer ? origin() : ''}${getBaseUrl(
      isServer
    )}/api/v4/orders/${storeOrderId}/track-delivery`,
    {
      headers: makeHeaders({ req })
    }
  )

export const updateCheckoutMetaApi = async ({
  req,
  storeOrderId,
  payload
}: {
  req: RequestType
  storeOrderId: string | string[]
  payload: Record<string, unknown>
}) => {
  return axiosInstance.patch(
    `${getBaseUrl(false)}/order/${storeOrderId}`,
    payload,
    {
      headers: makeHeaders({ req })
    }
  )
}

export const fetchReservationInfoApi = async ({
  menuId
}: {
  menuId: string
}) => {
  const getHeaders = makeHeaders({})
  return axios.get(
    `${getBaseUrl(
      true
    )}/api/v5/cart/menus/${menuId}/channels?channel-names=reserve`,
    {
      headers: getHeaders
    }
  )
}

export const getCTAConfig = async () =>
  axiosInstance.get(
    'https://shop-dynamic-config.s3.ap-southeast-1.amazonaws.com/cta-link.json'
  )

export const getMaxDaysPickerConfig = async () =>
  axiosInstance.get(
    'https://shop-dynamic-config.s3.ap-southeast-1.amazonaws.com/max-days-config.json'
  )

export const removeSelectedVoucherApi = async ({
  storeOrderId,
  voucherId
}: {
  storeOrderId: string
  voucherId: string
}) =>
  axiosInstance.delete(
    `${getBaseUrl(
      false
    )}/api/v4/cart/vouchers/${voucherId}?storeOrderId=${storeOrderId}`
  )

export const getAnnouncementBarConfig = async (isBeta) =>
  axiosInstance.get(
    `https://shop-dynamic-config.s3.ap-southeast-1.amazonaws.com/${
      isBeta ? 'beta-' : ''
    }announcement-bar-config.json`
  )
